import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { ProjectDataType, ProjectFeatureStatus } from "store/types/project";

/** A picked draft and the project it belongs to. */
export type ProjectDraftSelection = { projectId: string; documentId: string };

const initialProjectState: ProjectDataType = {
  name: "",
  description: "",
  created_at: "",
  is_deleted: false,
  production_type: "",
  project_id: "",
  archived: false,
  is_trial: false,
  documents: [
    {
      file_path: "",
      file_size: 0,
      is_parsed_successfully: false,
    },
  ],
  versions_count: 0,
  active_document_id: null,
  selected_document_id: null,
  selected_document_project_id: null,
  versions: undefined,
  features: {
    has_budget: false,
    max_budget: 0,
    min_budget: 0,
    production_state: null,
    has_coverage: false,
    has_character_breakdown: false,
    has_script_breakdown: false,
    has_schedule: false,
    project_id: "",
    project_feature_id: 0,
  },
  feature_statuses: undefined,
};

const projectSlice = createSlice({
  name: "project",
  initialState: initialProjectState,
  reducers: {
    setProjectData: (state, action: PayloadAction<ProjectDataType>) => {
      const { current_user_role } = state;
      // Preserve selected_document_id unless incoming payload provides it
      // The pick and its project always move together.
      const keepsSelection = action.payload.selected_document_id == null;
      const selected_document_id = keepsSelection
        ? (state.selected_document_id ?? null)
        : action.payload.selected_document_id;
      const selected_document_project_id = keepsSelection
        ? (state.selected_document_project_id ?? null)
        : (action.payload.selected_document_project_id ?? action.payload.project_id ?? null);
      return {
        ...action.payload,
        current_user_role,
        selected_document_id,
        selected_document_project_id,
      };
    },
    /** The draft picked in this session, always with the project it belongs to. */
    setSelectedDocumentId: (state, action: PayloadAction<ProjectDraftSelection | null>) => {
      state.selected_document_id = action.payload?.documentId ?? null;
      state.selected_document_project_id = action.payload?.projectId ?? null;
    },
    setProjectFeatureStatus: (state, action: PayloadAction<ProjectFeatureStatus>) => {
      const featureIndex = state.feature_statuses?.findIndex(
        (x) => x.feature_status_id === action.payload.id
      );
      if (featureIndex !== undefined && featureIndex >= 0) {
        state.feature_statuses![featureIndex].status = action.payload.status;
      }
    },
    updateProjectRole: (
      state,
      action: PayloadAction<"creator" | "editor" | "viewer" | "trial">
    ) => {
      state.current_user_role = action.payload;
    },
    logout: () => initialProjectState,
  },
});

export const {
  setProjectData,
  setProjectFeatureStatus,
  updateProjectRole,
  setSelectedDocumentId,
  logout,
} = projectSlice.actions;

/** The draft picked for `projectId`; null when there is no pick or it belongs to another project. */
export const selectSelectedDocumentIdForProject = (
  project:
    | Pick<ProjectDataType, "selected_document_id" | "selected_document_project_id">
    | undefined,
  projectId: string | number | null | undefined
): string | null => {
  if (!project?.selected_document_id || projectId == null || projectId === "") return null;
  return String(project.selected_document_project_id) === String(projectId)
    ? project.selected_document_id
    : null;
};

/** The session's draft pick as an action payload, to put back later. */
export const getSelectedDocument = (
  project:
    | Pick<ProjectDataType, "selected_document_id" | "selected_document_project_id">
    | undefined
): ProjectDraftSelection | null =>
  project?.selected_document_id && project.selected_document_project_id
    ? { projectId: project.selected_document_project_id, documentId: project.selected_document_id }
    : null;
export default projectSlice.reducer;
